import { useOfficeComponentContext } from '@office-start/component-context-service';
import { useFeatureGates } from '@office-start/feature-gates-service';
import { FeedbackPolicyValue } from '@office-start/feedback-sdk-types';
import { useIdentity } from '@office-start/identity-service';
import { KnownInstrumentationEvents } from '@office-start/instrumentation-types';
import { OfficeHomeContext } from '@office-start/office-home-context';
import { useUserEntitlements } from '@office-start/user-entitlements-service';
import { usePersistedUserStorage } from '@office-start/user-storage-service';
import { useHttpGet } from '@office-start/xhr-service';
import { useContext, useMemo } from 'react';
var StateToFeedbackMap = {
    true: FeedbackPolicyValue.Enabled,
    false: FeedbackPolicyValue.Disabled,
};
var resultToAdminPolicy = function (adminPolicyFetchResult) {
    return {
        ConnectedExperience: StateToFeedbackMap[String(adminPolicyFetchResult.Value.ConnectedExperience.State)],
        SendFeedback: StateToFeedbackMap[String(adminPolicyFetchResult.Value.SendFeedback.State)],
        ScreenShot: StateToFeedbackMap[String(adminPolicyFetchResult.Value.ScreenShot.State)],
        EmailCollection: StateToFeedbackMap[String(adminPolicyFetchResult.Value.EmailCollection.State)],
        LogCollection: StateToFeedbackMap[String(adminPolicyFetchResult.Value.LogCollection.State)],
        SendSurvey: StateToFeedbackMap[String(adminPolicyFetchResult.Value.SendSurvey.State)],
        LoopBizChatDocs: StateToFeedbackMap[String(adminPolicyFetchResult.Value.LoopBizChatDocs.State)],
        Loop: StateToFeedbackMap[String(adminPolicyFetchResult.Value.Loop.State)],
        OptionalConnectedExperiences: StateToFeedbackMap[String(adminPolicyFetchResult.Value.OptionalConnectedExperiences
            .State)],
        CopilotPinning: StateToFeedbackMap[String(adminPolicyFetchResult.Value.CopilotPinning.State)],
        WebSearch: StateToFeedbackMap[String(adminPolicyFetchResult.Value.WebSearch.State)],
    };
};
var useGetAdminPolicy = function (adminPolicyPath) {
    var _a, _b, _c;
    var identity = (_a = useIdentity()) === null || _a === void 0 ? void 0 : _a.data;
    var apiRoot = (_c = (_b = useOfficeComponentContext()) === null || _b === void 0 ? void 0 : _b.data) === null || _c === void 0 ? void 0 : _c.officeHomeApiRoot;
    var url = "".concat(apiRoot, "/").concat(adminPolicyPath);
    // Set empty query key to NOT fetch if we don't have an API root or policy path
    var queryKey = apiRoot && adminPolicyPath ? 'AdminPolicy' : '';
    var requestConfig = {
        responseType: "json" /* HttpResponseContentType.Json */,
        headers: {},
    };
    // Apply office identity
    requestConfig.headers["X-OfficeHome-UserId" /* OfficeHeader.UserId */] = identity === null || identity === void 0 ? void 0 : identity.puid;
    requestConfig.headers["X-OfficeHome-TenantId" /* OfficeHeader.TenantId */] = identity === null || identity === void 0 ? void 0 : identity.tenantId;
    return useHttpGet(queryKey, KnownInstrumentationEvents.AdminPolicyServerRequest, "Admin_Policy_Server" /* KnownRequestInstrumentationEvents.AdminPolicyServerRequest */, url, requestConfig, 'OfficeHome');
};
/**
 * Use the admin policy from cache
 * @param adminPolicyCacheKey Key to look for the policy in cache
 * @returns null if no cached policy, undefined while loading
 */
var useCachedAdminPolicy = function (adminPolicyCacheKey) {
    var _a, _b, _c;
    var entitlements = (_b = (_a = useUserEntitlements()) === null || _a === void 0 ? void 0 : _a.data) === null || _b === void 0 ? void 0 : _b.result;
    var adminPolicyQuery = usePersistedUserStorage(adminPolicyCacheKey, false, (entitlements === null || entitlements === void 0 ? void 0 : entitlements.isPwa) || (entitlements === null || entitlements === void 0 ? void 0 : entitlements.isPwaInWebView2) ? 1 : 0);
    // Return undefined if there aren't yet results to be had...
    if (adminPolicyQuery.isInitialData || adminPolicyQuery.isLoading) {
        return undefined;
    }
    var adminPolicyFetchResult = (_c = adminPolicyQuery === null || adminPolicyQuery === void 0 ? void 0 : adminPolicyQuery.data) === null || _c === void 0 ? void 0 : _c.result;
    if ((adminPolicyCacheKey && !adminPolicyFetchResult) ||
        !(adminPolicyFetchResult === null || adminPolicyFetchResult === void 0 ? void 0 : adminPolicyFetchResult.Value)) {
        // Fail
        return null;
    }
    var result;
    var lastUpdate = new Date(adminPolicyFetchResult.LastUpdated);
    var expireTime = lastUpdate.getTime() +
        adminPolicyFetchResult.CheckInInterval * 60 * 1000;
    var currentTime = Date.now();
    if (expireTime < currentTime) {
        result = null;
    }
    else {
        // $TODO: check for missing policies in case new policies added
        result = resultToAdminPolicy(adminPolicyFetchResult);
    }
    return result;
};
export var useAdminPolicy = function () {
    var _a, _b, _c, _d, _e;
    var identity = (_a = useIdentity()) === null || _a === void 0 ? void 0 : _a.data;
    var featureGates = (_b = useFeatureGates()) === null || _b === void 0 ? void 0 : _b.data;
    var adminPolicy = useContext(OfficeHomeContext).adminPolicy;
    // Do not attempt to retrieve a cache key if it is undefined
    var cachedPolicy = useCachedAdminPolicy('AdminPolicy');
    var useAdminPolicyFromContext = (_c = featureGates === null || featureGates === void 0 ? void 0 : featureGates.result.aiHubGates) === null || _c === void 0 ? void 0 : _c.aiHubAdminPolicyEnabled;
    // Do not fetch the admin policy if...
    //  user is an MSA user
    //  adminPolicy from context
    //  cachedPolicy is loading
    //  cachedPolicy exists
    var adminPolicyApiPath = (identity === null || identity === void 0 ? void 0 : identity.isMsa) ||
        (adminPolicy && useAdminPolicyFromContext) ||
        cachedPolicy ||
        cachedPolicy === undefined
        ? ''
        : 'api/adminpolicy';
    var uncachedPolicy = (_e = (_d = useGetAdminPolicy(adminPolicyApiPath)) === null || _d === void 0 ? void 0 : _d.data) === null || _e === void 0 ? void 0 : _e.data;
    return useMemo(function () {
        if (!identity || identity.isMsa) {
            return undefined;
        }
        // return if we get adminPolicy from context
        if (adminPolicy && useAdminPolicyFromContext) {
            return resultToAdminPolicy(adminPolicy);
        }
        if (cachedPolicy) {
            return cachedPolicy;
        }
        if (!(uncachedPolicy === null || uncachedPolicy === void 0 ? void 0 : uncachedPolicy.Value)) {
            return null;
        }
        return resultToAdminPolicy(uncachedPolicy);
    }, [cachedPolicy, uncachedPolicy, identity, adminPolicy]);
};
//# sourceMappingURL=useAdminPolicy.js.map